// New-message alerts for the admin: the unread count (checked every 30 seconds and when
// the tab comes back into view), a short ding and a desktop notification when it goes up.
import { useEffect, useRef, useState } from 'react';
import { api } from './api';

const POLL_MS = 30000;

const OFF_KEY = 'mm-admin-alerts-off';

// Alerts (sound and pop-ups) can be switched off on this computer.
export function alertsEnabled() {
  try {
    return localStorage.getItem(OFF_KEY) !== '1';
  } catch {
    return true;
  }
}
export function setAlertsEnabled(on) {
  try {
    if (on) localStorage.removeItem(OFF_KEY); else localStorage.setItem(OFF_KEY, '1');
  } catch {
    // Not remembered, but still applies until the page reloads.
  }
}

// Browsers only allow sound after a click or key press on the page, so the sound is
// readied on the first one; later dings (from the 30-second check) can then play.
let audio = null;
function audioContext() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audio = audio || new Ctx();
  if (audio.state === 'suspended') audio.resume().catch(() => {});
  return audio;
}
let primed = false;
function primeAudio() {
  if (primed || typeof document === 'undefined') return;
  primed = true;
  const unlock = () => {
    try { audioContext(); } catch { /* no sound */ }
    document.removeEventListener('pointerdown', unlock, true);
    document.removeEventListener('keydown', unlock, true);
  };
  document.addEventListener('pointerdown', unlock, true);
  document.addEventListener('keydown', unlock, true);
}

// Two soft notes made in the browser, so there's no sound file to load.
export function ding() {
  try {
    if (!audioContext()) return;
    const start = audio.currentTime;
    [[880, 0], [1320, 0.13]].forEach(([freq, delay]) => {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start + delay);
      gain.gain.exponentialRampToValueAtTime(0.25, start + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + delay + 0.35);
      osc.connect(gain).connect(audio.destination);
      osc.start(start + delay);
      osc.stop(start + delay + 0.4);
    });
  } catch {
    // No sound is fine.
  }
}

export const alertsSupported = () => typeof window !== 'undefined' && 'Notification' in window;
export const alertsOn = () => alertsSupported() && window.Notification.permission === 'granted';

// Browsers only ask after a click, so this is called from the "Turn on alerts" button.
export async function askForAlerts() {
  if (!alertsSupported()) return 'unsupported';
  const result = await window.Notification.requestPermission();
  if (result === 'granted') ding();
  return result;
}

export function showAlert(latest, count) {
  if (!alertsEnabled() || !alertsOn() || !document.hidden) return;
  try {
    const who = latest ? latest.name : '';
    const what = latest && latest.source === 'email' ? latest.subject : latest && latest.category;
    const n = new window.Notification(count > 1 ? `${count} new messages · Market Matrix` : 'New message · Market Matrix', {
      body: [who, what].filter(Boolean).join(': ') || 'Open your admin Inbox',
      icon: '/logo192.png',
      tag: 'mm-inbox',
    });
    n.onclick = () => {
      window.focus();
      if (window.location.pathname !== '/admin') window.location.assign('/admin');
      n.close();
    };
  } catch {
    // Some browsers only allow notifications from a service worker; the ding still plays.
  }
}

// A new message came in: the ding and the pop-up, unless alerts are switched off.
export function announce(count, latest) {
  if (!alertsEnabled()) return;
  ding();
  showAlert(latest, count);
}

// The unread count, for admins only. `onArrival(count, latest)` runs whenever someone writes
// in (a new message or a follow-up), but not on the first check, so opening a page with old
// unread messages stays quiet.
export function useUnreadMessages(enabled, onArrival) {
  const [count, setCount] = useState(0);
  const lastAt = useRef(undefined);
  const callback = useRef(onArrival);
  callback.current = onArrival;

  useEffect(() => {
    if (!enabled) return undefined;
    primeAudio();
    let stopped = false;
    const check = async () => {
      try {
        const r = await api('/admin/messages/unread');
        if (stopped) return;
        const at = r.latestAt ? new Date(r.latestAt).getTime() : 0;
        if (lastAt.current !== undefined && at > lastAt.current && callback.current) callback.current(r.count, r.latest);
        lastAt.current = Math.max(at, lastAt.current || 0);
        setCount(r.count);
      } catch {
        // Try again next time.
      }
    };
    check();
    const timer = setInterval(check, POLL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stopped = true; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [enabled]);

  return [count, setCount];
}
